/**
 * Idle-detection context.
 *
 * Tracks user activity at the document level and flips an `idle`
 * boolean on after 15 minutes of inactivity (or immediately when the
 * tab is hidden via the page-visibility API). When idle, the
 * WebSocket layer closes its connections and the scheduler on the
 * backend naturally drops the user's tickers out of its active set —
 * so a user who leaves a tab open overnight doesn't keep paying for
 * Tradier chain pulls and VM bandwidth.
 *
 * Why context, not a per-hook arg: the two WS consumers (Options page
 * + AmbientTicker on every authed page) both need to honour the same
 * idle signal. Plumbing a `paused` boolean through every caller would
 * be verbose; a single source of truth at the provider level is
 * cleaner. `useWebSocket` reads `useIdle()` internally, so callers
 * don't change.
 *
 * Why visibilitychange counts as idle: a hidden tab can't show data
 * to the user anyway. Pausing the moment the tab is backgrounded
 * avoids 15 minutes of wasted polling while the user is in another
 * tab. The page-visibility callback also handles "user comes back" —
 * visible → reset timer + clear idle, so the WS reconnects
 * immediately on tab focus.
 *
 * Resume contract: ANY user input (mouse / keyboard / touch / scroll)
 * or visibility-back resets idle to false. No "click here to resume"
 * UI — the page just comes back to life when the user does.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

const IDLE_AFTER_MS = 15 * 60 * 1000; // 15 minutes

interface IdleContextValue {
  /** True after IDLE_AFTER_MS without user input, or when the tab is hidden. */
  idle: boolean;
  /** Force-resume (used by the resume-now button on the paused chip). */
  resume: () => void;
}

const IdleContext = createContext<IdleContextValue>({
  idle: false,
  resume: () => undefined,
});

interface IdleProviderProps {
  children: React.ReactNode;
}

export const IdleProvider: React.FC<IdleProviderProps> = ({ children }) => {
  const [idle, setIdle] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    // Only re-render if we were actually idle — otherwise every keystroke
    // would trigger a no-op render. React skips a setState when the
    // updater returns the existing reference.
    setIdle(prev => (prev ? false : prev));
    timerRef.current = setTimeout(() => setIdle(true), IDLE_AFTER_MS);
  }, []);

  useEffect(() => {
    // Document-level activity listeners. `passive: true` is appropriate
    // because we never call preventDefault — we just observe events
    // and reset our own timer.
    const activityEvents: (keyof DocumentEventMap)[] = [
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
      'wheel',
    ];
    activityEvents.forEach(e =>
      document.addEventListener(e, resetTimer, { passive: true }),
    );

    const onVisibilityChange = () => {
      if (document.hidden) {
        // Tab backgrounded: instantly idle so we stop pulling data.
        // No need to wait 15 minutes when we know nobody's looking.
        if (timerRef.current) clearTimeout(timerRef.current);
        setIdle(prev => (prev ? prev : true));
      } else {
        // Tab back: reset timer + clear idle so the WS reconnects.
        resetTimer();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Also clear idle on `focus` — covers cmd-tab back into a tab that
    // was already visible (visibilitychange doesn't fire in that case
    // on some browsers).
    const onFocus = () => resetTimer();
    window.addEventListener('focus', onFocus);

    // Start the timer fresh on mount.
    resetTimer();

    return () => {
      activityEvents.forEach(e =>
        document.removeEventListener(e, resetTimer),
      );
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [resetTimer]);

  return (
    <IdleContext.Provider value={{ idle, resume: resetTimer }}>
      {children}
    </IdleContext.Provider>
  );
};

export const useIdle = (): IdleContextValue => useContext(IdleContext);
